import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, ShieldOff } from 'lucide-react';
import { useToast } from './use-toast';

interface CaptchaToggleProps {
  isMobile?: boolean;
}

export const CaptchaToggle = ({ isMobile = false }: CaptchaToggleProps) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Load initial state from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('nosh_disable_captcha');
    setIsDisabled(stored === 'true');
  }, []);

  const handleToggle = async () => {
    setIsLoading(true);
    
    try {
      const newState = !isDisabled;
      
      // Update localStorage immediately
      localStorage.setItem('nosh_disable_captcha', newState.toString());
      setIsDisabled(newState);
      
      // Show toast with instructions
      if (newState) {
        toast({
          title: "🎯 Hackathon Mode Enabled!",
          description: "CAPTCHA is now disabled. Refresh the page to see changes on login/signup forms.",
          duration: 4000,
        });
      } else {
        toast({
          title: "🛡️ Security Mode Enabled!",
          description: "CAPTCHA is now enabled. Refresh the page to see changes on login/signup forms.",
          duration: 4000,
        });
      }
      
      // Suggest page refresh for immediate effect
      setTimeout(() => {
        const shouldRefresh = window.confirm(
          `CAPTCHA has been ${newState ? 'disabled' : 'enabled'}. Would you like to refresh the page to apply changes immediately?`
        );
        if (shouldRefresh) {
          window.location.reload();
        }
      }, 2000);
      
    } catch (error) {
      console.error('Error toggling CAPTCHA:', error);
      toast({
        title: "Error",
        description: "Failed to toggle CAPTCHA setting. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      whileHover={{ scale: isMobile ? 1 : 1.01 }}
      className="flex items-center justify-between p-4 bg-muted rounded-lg transition-all duration-200"
    >
      <div className="flex items-center space-x-3">
        <div className={`p-2 rounded-lg ${
          isDisabled 
            ? 'bg-amber-100 dark:bg-amber-900/30' 
            : 'bg-green-100 dark:bg-green-900/30'
        }`}>
          {isDisabled ? (
            <ShieldOff className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          ) : (
            <Shield className="h-5 w-5 text-green-600 dark:text-green-400" />
          )}
        </div>
        <div>
          <p className="font-medium">
            🎯 Hackathon Mode
          </p>
          <p className="text-sm text-muted-foreground">
            {isDisabled 
              ? "CAPTCHA disabled - Easy judge access" 
              : "CAPTCHA enabled - Full security"
            }
          </p>
          {isDisabled && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Perfect for hackathon demos and judging
            </p>
          )}
        </div>
      </div>
      
      <div className="flex items-center space-x-2">
        {/* Status indicator */}
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
          isDisabled 
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' 
            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
        }`}>
          {isDisabled ? 'Disabled' : 'Enabled'}
        </div>
        
        {/* Toggle switch */}
        <label className="relative inline-flex items-center cursor-pointer">
          <input 
            type="checkbox" 
            checked={isDisabled}
            onChange={handleToggle}
            disabled={isLoading}
            className="sr-only peer" 
          />
          <div className={`w-11 h-6 rounded-full peer peer-focus:outline-none transition-all peer-disabled:opacity-50 ${
            isDisabled 
              ? 'bg-amber-200 dark:bg-amber-800' 
              : 'bg-gray-200 dark:bg-gray-700'
          } peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all ${
            isDisabled 
              ? 'peer-checked:bg-amber-500' 
              : 'peer-checked:bg-green-500'
          }`}></div>
        </label>
      </div>
    </motion.div>
  );
}; 